import { registerOrganizationRequestSchema } from '@goodneighbor/api/routes/organizations/registration/registration.schema';
import { type EventLocation } from '@goodneighbor/api/schemas/maps';
import { z } from 'zod';
import { cleanPhoneNumber } from '@/services/phone';

const phoneValidation = z
  .string()
  .trim()
  .min(1, { message: 'Phone is required' })
  .refine(
    (value) => {
      const cleaned = cleanPhoneNumber(value);
      const digits = cleaned.startsWith('+') ? cleaned.slice(1) : cleaned;
      return digits.length >= 10 && digits.length <= 15;
    },
    { message: 'Enter a valid phone number' }
  );

const slugValidation = z
  .string()
  .min(3, { message: 'URL slug must be at least 3 characters' })
  .max(60, { message: 'URL slug must be at most 60 characters' })
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message:
      'URL must be lowercase letters, numbers, and hyphens only (no leading/trailing hyphens)'
  });

const websiteUrlValidation = z
  .string()
  .trim()
  .transform((val) => val.replace(/^https?:\/\//i, ''))
  .refine(
    (val) => {
      if (!val) return true;
      const domainRegex =
        /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/.*)?$/;
      return domainRegex.test(val);
    },
    { message: 'Please enter a valid domain (e.g., example.com)' }
  )
  .transform((val) => {
    if (!val) return '';
    return `https://${val}`;
  });

export const partnerInterestFormSchema = registerOrganizationRequestSchema
  .extend({
    organizationName: z
      .string()
      .trim()
      .min(2, { message: 'Organization name is required' }),
    slug: slugValidation,
    websiteUrl: websiteUrlValidation.optional(),
    einNumber: z
      .union([
        z.literal(''),
        z
          .string()
          .trim()
          .regex(/^\d+$/, { message: 'EIN must contain only numbers' })
      ])
      .optional(),
    organizationType: z
      .string()
      .min(1, { message: 'Please select an organization type' }),
    streetAddress: z
      .string()
      .trim()
      .min(1, { message: 'Street address is required' }),
    organizationLocation: z.custom<EventLocation>().optional(),
    firstName: z.string().trim().min(1, { message: 'First name is required' }),
    lastName: z.string().trim().min(1, { message: 'Last name is required' }),
    email: z
      .string()
      .trim()
      .min(1, { message: 'Email is required' })
      .email({ message: 'Enter a valid email address' }),
    phone: phoneValidation,
    hasMultipleLocations: z.enum(['Yes', 'No'], {
      required_error: 'Please select Yes or No'
    }),
    numberOfLocations: z.number().optional(),
    networkDenominationAssociation: z.enum(['Yes', 'No'], {
      required_error: 'Please select Yes or No'
    }),
    networkDenomination: z.string().trim().optional(),
    participationInterests: z.array(z.string()).optional(),
    participationInterestOther: z.string().optional(),
    participationInterestGoodsServices: z.string().optional(),
    languagePreference: z.string().optional(),
    languagePreferenceOther: z.string().optional(),
    referralSource: z.string().optional(),
    referralSourceOther: z.string().optional(),
    eventHostAgreementAccepted: z.boolean().refine((val) => val === true, {
      message:
        'You must agree to the Event Host and Partner Agreement to continue'
    }),
    privacyPolicyAccepted: z.boolean().refine((val) => val === true, {
      message: 'You must accept the privacy policy and terms to continue'
    })
  })
  .superRefine((values, ctx) => {
    const hasWebsite = values.websiteUrl && values.websiteUrl.trim() !== '';
    const hasEin = values.einNumber && values.einNumber.trim() !== '';

    if (!hasWebsite && !hasEin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A website or EIN is required',
        path: ['websiteUrl']
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A website or EIN is required',
        path: ['einNumber']
      });
    }

    if (
      values.networkDenominationAssociation === 'Yes' &&
      !values.networkDenomination?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please specify your network or denomination',
        path: ['networkDenomination']
      });
    }

    if (
      values.hasMultipleLocations === 'Yes' &&
      (values.numberOfLocations === undefined || values.numberOfLocations < 2)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please specify the number of locations (must be 2 or more)',
        path: ['numberOfLocations']
      });
    }
  })
  .transform((values) => ({
    ...values,
    numberOfLocations:
      values.hasMultipleLocations === 'No' ? 1 : values.numberOfLocations
  }));

export type PartnerInterestFormValues = z.infer<
  typeof partnerInterestFormSchema
>;

export const partnerRegistrationSchema = partnerInterestFormSchema;
export type PartnerRegistrationFormValues = PartnerInterestFormValues;
